<template>
  <div class="modal-overlay" @click="handleOverlayClick">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h3><i class="fa fa-book"></i> Send Wiki Article</h3>
        <button class="close-btn" @click="$emit('cancel')">
          <i class="fa fa-times"></i>
        </button>
      </div>
      
      <div class="modal-body">
        <div class="search-section">
          <div class="search-input-container">
            <i class="fa fa-search"></i>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search wiki articles..."
              class="search-input"
              @input="filterWikis"
              ref="searchInput"
            />
          </div>
        </div>
        
        <div class="wiki-list" v-if="filteredWikis.length > 0">
          <div 
            v-for="wiki in filteredWikis" 
            :key="wiki.WikiPageID"
            class="wiki-item"
            :class="{ 'selected': selectedWiki && selectedWiki.WikiPageID === wiki.WikiPageID }"
            @click="selectWiki(wiki)"
          >
            <div class="wiki-info">
              <div class="wiki-title">{{ wiki.Name }}</div>
              <div class="wiki-meta">
                <span class="wiki-category" v-if="wiki.Tags">{{ wiki.Tags }}</span>
                <span class="wiki-updated" v-if="wiki.UpdatedAt">
                  Updated {{ formatDate(wiki.UpdatedAt) }}
                </span>
              </div>
            </div>
            <div class="wiki-actions">
              <i class="fa fa-external-link" title="Preview" @click.stop="previewWiki(wiki)"></i>
            </div>
          </div>
        </div>
        
        <div v-else-if="loading" class="loading-state">
          <i class="fa fa-spinner fa-spin"></i>
          <span>Loading wiki articles...</span>
        </div>
        
        <div v-else class="empty-state">
          <i class="fa fa-search"></i>
          <span>No wiki articles found</span>
        </div>
        
        <!-- Selected Wiki Preview -->
        <div v-if="selectedWiki" class="selected-preview">
          <h4>Selected Article:</h4>
          <div class="preview-card">
            <strong>{{ selectedWiki.Name }}</strong>
            <div class="preview-meta">
              <span v-if="selectedWiki.Tags">Tags: {{ selectedWiki.Tags }}</span>
              <span v-if="selectedWiki.UpdatedAt">Updated: {{ formatDate(selectedWiki.UpdatedAt) }}</span>
            </div>
            <div class="preview-url">
              <i class="fa fa-link"></i>
              <span>{{ getWikiUrl(selectedWiki) }}</span>
            </div>
          </div>
        </div>
      </div>
      
      <div class="modal-footer">
        <button 
          class="btn btn-primary"
          @click="sendWiki"
          :disabled="!selectedWiki"
        >
          <i class="fa fa-paper-plane"></i>
          Send Wiki Link
        </button>
        <button class="btn btn-secondary" @click="$emit('cancel')">
          Cancel
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import mixin from '@/mixin.js'
import { withoutReviewRequestTemplates } from '@/utils/wikiTags'

export default {
  name: 'WikiSelectionModal',
  mixins: [mixin],
  data() {
    return {
      searchQuery: '',
      selectedWiki: null,
      filteredWikis: [],
      isLoading: true,
      wikiPages: [] // Store wikis locally like PatientSidebar
    }
  },
  computed: {
    availableWikis() {
      const wikis = this.wikiPages || []
      return wikis.filter(wiki => {
        // If we have IsSmsTemplate field, prefer SMS templates, otherwise show all
        const isSmsAppropriate = wiki.IsSmsTemplate === 1 || wiki.IsSmsTemplate === '1' || 
                                 !wiki.hasOwnProperty('IsSmsTemplate')
        
        return isSmsAppropriate
      })
    }
  },
  async mounted() {
    // Focus search input
    this.$nextTick(() => {
      this.$refs.searchInput?.focus()
    })
    
    // Load wikis if not already loaded
    await this.loadWikis()
    this.filterWikis()
  },
  methods: {
    async loadWikis() {
      try {
        this.isLoading = true
        
        // Check if wikis are already loaded
        if (!this.availableWikis.length) {
          // Load wikis from the store action (same as PatientSidebar)
          // First try with sms-template tag, then fallback to all wikis if none found
          let result = await this.$store.dispatch('wiki/getWikiPages', {
            siteID: this.siteID,
            tags: 'sms-template'
          })
          
          // If no SMS template wikis found, try loading all wikis
          if (!result || !Array.isArray(result) || result.length === 0 || !result[0] || result[0].length === 0) {
            console.log('[WikiModal] No SMS template wikis found, trying all wikis...')
            result = await this.$store.dispatch('wiki/getWikiPages', {
              siteID: this.siteID
            })
          }
          console.log('[WikiModal] Raw result from getWikiPages:', result)
          // Store wikis locally - the action returns response.data, wikis are in first element
          if (result && Array.isArray(result) && result.length > 0) {
            // Reviews templates stay out: only the Reviews send path can fill their {{review_link}}.
            this.wikiPages = withoutReviewRequestTemplates(result[0])
            console.log('[WikiModal] Loaded wikis:', this.wikiPages)
            console.log('[WikiModal] First wiki object structure:', this.wikiPages[0])
          }
        }
        
        this.isLoading = false
      } catch (error) {
        console.error('Failed to load wiki pages:', error)
        this.isLoading = false
      }
    },
    
    filterWikis() {
      if (!this.searchQuery.trim()) {
        this.filteredWikis = this.availableWikis.slice(0, 20) // Limit to first 20
        return
      }
      
      const query = this.searchQuery.toLowerCase()
      this.filteredWikis = this.availableWikis.filter(wiki => 
        wiki.Name?.toLowerCase().includes(query) ||
        wiki.Tags?.toLowerCase().includes(query)
      ).slice(0, 20)
    },
    
    selectWiki(wiki) {
      this.selectedWiki = wiki
    },
    
    previewWiki(wiki) {
      // Open wiki page in new window for preview
      const wikiUrl = `/app/wikiview/${wiki.WikiPageID}`
      window.open(wikiUrl, '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes')
    },
    
    sendWiki() {
      if (!this.selectedWiki) return
      
      this.$emit('send', {
        WikiPageID: this.selectedWiki.WikiPageID,
        Title: this.selectedWiki.Title,
        Description: this.selectedWiki.Description,
        url: this.getWikiUrl(this.selectedWiki)
      })
    },
    
    getWikiUrl(wiki) {
      // Construct the wiki URL - adjust based on your wiki system
      const baseUrl = process.env.VUE_APP_WIKI_BASE_URL || window.location.origin
      return `${baseUrl}/wiki/${wiki.WikiPageID}`
    },
    
    formatDate(dateString) {
      if (!dateString) return ''
      return moment(dateString).format('MMM D, YYYY')
    },
    
    handleOverlayClick() {
      this.$emit('cancel')
    }
  }
}
</script>

<style lang="scss" scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  
  h3 {
    margin: 0;
    color: #333;
    font-size: 18px;
    
    .fa {
      margin-right: 8px;
      color: #1976d2;
    }
  }
  
  .close-btn {
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    color: #666;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
      background: #f5f5f5;
    }
  }
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.search-section {
  margin-bottom: 20px;
  
  .search-input-container {
    position: relative;
    
    .fa-search {
      position: absolute;
      left: 12px;
      top: 50%;
      transform: translateY(-50%);
      color: #666;
    }
    
    .search-input {
      width: 100%;
      padding: 12px 12px 12px 40px;
      border: 1px solid #ddd;
      border-radius: 8px;
      font-size: 14px;
      outline: none;
      
      &:focus {
        border-color: #1976d2;
        box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
      }
    }
  }
}

.wiki-list {
  .wiki-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    margin-bottom: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
      border-color: #1976d2;
      background: #f8f9ff;
    }
    
    &.selected {
      border-color: #1976d2;
      background: #e3f2fd;
    }
    
    .wiki-info {
      flex: 1;
      
      .wiki-title {
        font-weight: 600;
        color: #333;
        margin-bottom: 4px;
      }
      
      .wiki-description {
        color: #666;
        font-size: 13px;
        line-height: 1.4;
        margin-bottom: 8px;
      }
      
      .wiki-meta {
        display: flex;
        gap: 12px;
        font-size: 12px;
        color: #999;
        
        .wiki-category {
          background: #f0f0f0;
          padding: 2px 6px;
          border-radius: 4px;
        }
      }
    }
    
    .wiki-actions {
      color: #666;
      
      .fa {
        font-size: 14px;
        cursor: pointer;
        padding: 4px;
        border-radius: 4px;
        transition: all 0.2s ease;
        
        &:hover {
          color: #007bff;
          background-color: #f8f9fa;
          transform: scale(1.1);
        }
      }
    }
  }
}

.loading-state, .empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #666;
  
  .fa {
    font-size: 24px;
    margin-bottom: 8px;
  }
}

.selected-preview {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
  
  h4 {
    margin: 0 0 12px 0;
    font-size: 14px;
    color: #666;
  }
  
  .preview-card {
    background: #f8f9ff;
    border: 1px solid #e3f2fd;
    border-radius: 8px;
    padding: 16px;
    
    strong {
      color: #1976d2;
      display: block;
      margin-bottom: 8px;
    }
    
    p {
      margin: 0 0 12px 0;
      color: #666;
      font-size: 13px;
    }
    
    .preview-url {
      display: flex;
      align-items: center;
      font-size: 12px;
      color: #999;
      
      .fa {
        margin-right: 6px;
      }
    }
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px;
  border-top: 1px solid #e0e0e0;
  
  .btn {
    padding: 10px 20px;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s ease;
    
    &.btn-primary {
      background: #1976d2;
      color: white;
      
      &:hover:not(:disabled) {
        background: #1565c0;
      }
      
      &:disabled {
        background: #ccc;
        cursor: not-allowed;
      }
    }
    
    &.btn-secondary {
      background: #f5f5f5;
      color: #666;
      
      &:hover {
        background: #e0e0e0;
      }
    }
  }
}

// Mobile responsiveness
@media (max-width: 768px) {
  .modal-overlay {
    padding: 10px;
  }
  
  .modal-content {
    max-height: 90vh;
  }
  
  .modal-header, .modal-body, .modal-footer {
    padding: 16px;
  }
}
</style>
